import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { submitConsultation } from "@/lib/api";

const INVESTMENT_TYPES = [
  "Land Development",
  "Property Flipping",
  "International Investment",
  "Project Management",
  "General Inquiry",
];

const BUDGETS = [
  "Under $250K",
  "$250K – $500K",
  "$500K – $1M",
  "$1M – $5M",
  "$5M+",
];

export default function ConsultationDialog({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    investment_type: INVESTMENT_TYPES[0],
    budget: BUDGETS[1],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleClose = () => {
    if (submitting) return;
    onClose();
    setTimeout(() => {
      setSuccess(false);
      setError("");
    }, 350);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitConsultation(form);
      setSuccess(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        investment_type: INVESTMENT_TYPES[0],
        budget: BUDGETS[1],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="consultation-dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full max-w-2xl bg-[#12253D] border border-white/10 max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              data-testid="consult-dialog-close"
              onClick={handleClose}
              className="absolute top-5 right-5 z-10 text-[#8FA3B8] hover:text-white transition"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {!success ? (
              <form onSubmit={handleSubmit} className="p-8 md:p-12">
                <div className="overline mb-4">Book a consultation</div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-2">
                  Let's discuss your next project.
                </h3>
                <p className="text-[#8FA3B8] text-sm mb-8 max-w-md">
                  Share a few details and our team will reach out within 24 hours with next steps tailored to your goals.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="field-label" htmlFor="cf-name">Full Name *</label>
                    <input
                      id="cf-name"
                      data-testid="consult-input-name"
                      className="field-input"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={update("name")}
                      required
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="cf-email">Email *</label>
                    <input
                      id="cf-email"
                      type="email"
                      data-testid="consult-input-email"
                      className="field-input"
                      placeholder="jane@example.com"
                      value={form.email}
                      onChange={update("email")}
                      required
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="cf-phone">Phone</label>
                    <input
                      id="cf-phone"
                      data-testid="consult-input-phone"
                      className="field-input"
                      placeholder="+1 (780) 000-0000"
                      value={form.phone}
                      onChange={update("phone")}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="cf-type">Interest</label>
                    <select
                      id="cf-type"
                      data-testid="consult-input-type"
                      className="field-input"
                      value={form.investment_type}
                      onChange={update("investment_type")}
                    >
                      {INVESTMENT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-[#12253D]">{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="cf-budget">Investment Range</label>
                    <select
                      id="cf-budget"
                      data-testid="consult-input-budget"
                      className="field-input"
                      value={form.budget}
                      onChange={update("budget")}
                    >
                      {BUDGETS.map((b) => (
                        <option key={b} value={b} className="bg-[#12253D]">{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="cf-message">Tell us about your project *</label>
                    <textarea
                      id="cf-message"
                      data-testid="consult-input-message"
                      rows={4}
                      className="field-input resize-none"
                      placeholder="Goals, timelines, locations of interest..."
                      value={form.message}
                      onChange={update("message")}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div data-testid="consult-error" className="mt-6 text-sm text-red-400 border border-red-500/30 px-4 py-3">
                    {error}
                  </div>
                )}

                <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-xs text-[#8FA3B8] max-w-xs">
                    By submitting, you agree we may contact you about your inquiry. We never share your information.
                  </p>
                  <button
                    type="submit"
                    data-testid="consult-submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Submit Request"}
                    {!submitting && <span className="btn-arrow">→</span>}
                  </button>
                </div>
              </form>
            ) : (
              <div data-testid="consult-success" className="p-10 md:p-16 text-center">
                <div className="mx-auto mb-8 w-16 h-16 border border-[#D4AF37] flex items-center justify-center">
                  <Check className="text-[#D4AF37]" size={28} />
                </div>
                <div className="overline mb-3">Request received</div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">
                  Thank you.
                </h3>
                <p className="text-[#8FA3B8] max-w-md mx-auto mb-8">
                  Your consultation request has been logged. A member of our team will be in touch within 24 hours.
                </p>
                <button
                  type="button"
                  data-testid="consult-success-close"
                  onClick={handleClose}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
